<template lang="pug">
#send-message-group-list(v-cloak)
  .box
    .box-header
      .d-flex.flex-row
        .d-flex.flex-column.flex-fill
          .d-flex.flex-row.align-items-center
            .title.mr-2
              span 宛先グループ情報一覧
            HelpIcon(title="宛先グループについて")
              template(v-slot:text)
                span
                  span 宛先グループを作成し、所属家族または所属施設関係者を設定できます。
                  br
                  span 宛先グループは、
                  strong メッセージ送信画面 「新規送信」タブ
                  span の宛先を指定するときに使用できます。
          .message
            span 一般ユーザーは宛先グループの追加・修正・削除と所属メンバーの変更ができません。
            br 
            span 管理者ユーザーが行ってください。
        .buttons.d-flex.flex-row-reverse.justify-content-end.flex-wrap(v-if="_showControlButton")
          button.btn.add-btn(@click="gotoSendMessageGroupRegister") 追加
    .box-body(style="padding-top:0;")
      .d-flex.justify-content-end
        .refresh-icon(@click.stop="refresh" title="再読み込みします")
      .mb-2
      SendMessageGroupTable(
        v-model="items"
        :fields="fields"
        :totalRows.sync="rows"
        :currentPage.sync="currentPage"
        :perPage="perPage"
        :isLoading="isLoading"
        @onEdit="gotoSendMessageGroupEdit"
        @onDelete="showConfirmDeleteModal"
        @onList="showSendMessageGroupMemberModal"
        @onMemberEdit="gotoSendMessageGroupMemberEdit"
        @onCsvDownload="onCsvDownload"
      )
      .table-options.d-flex.flex-row.justify-content-end.align-items-center
        // ページあたりの行数
        span.table-option-text 1ページの行数:
        .per-page
          b-form-select(
            id="per-page-select"
            v-model="perPage"
            :options="pageOptions"
            size="md"
            @change="selectPerPage"
          )
        // ページネーション
        b-pagination.m-0(
          v-model="currentPage"
          :total-rows="rows"
          :per-page="perPage"
          :disabled="isLoading"
          @change="selectPage"
          hide-goto-end-buttons
        )
  SendMessageGroupFamilyDetailModal(v-model="modalGroupMembers" :group-name.sync="modalGroupName" ref="sendMessageGroupFamilyDetailModal")
  SendMessageGroupOtherUserGroupMemberDetailModal(v-model="modalGroupMembers" :group-name.sync="modalGroupName" 
    :groupTypeNameMap="otherUserGroupTypeNameMap" ref="sendMessageGroupOtherUserGroupMemberDetailModal")
  OkCancelModal(v-model="showDeleteModal" :title="deleteMessage" @ok="deleteSendMessageGroup")
  OkModal(v-model="showDeleteSuccessModal" title="削除に成功しました。")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Watch, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import { needRefreshesStore } from "@/store/store";
import { SendMessagesState } from "@/store/NeedRefreshes";
import { errorHandle } from "@/errorHandler";

import Setting from "../../../../public/setting.json";
import { PcUrl } from "@/commons/constants/PcUrl";
import SendMessageGroupsService, { GroupType, SendMessageGroupsGetModel } from "@/commons/services/SendMessageGroups";
import SendMessageGroupMembersService, {
  SendMessageGroupMembersFamilyGetModel,
  SendMessageGroupMembersOtherUserGroupMemberGetModel
} from "@/commons/services/SendMessageGroupMembers";
import OtherUserGroupTypesService from "@/commons/services/OtherUserGroupTypes";
import { Role } from "@/commons/services/Authorization";
import OtherUserGroupTypeModel from "@/commons/models/OtherUserGroupType";
import SystemError from "@/commons/models/SystemError";
import { SenderType } from "@/commons/services/Families";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import HelpIcon from "@/pc/basics/HelpIcon.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import SendMessageGroupFamilyDetailModal from "@/pc/components/modals/SendMessageGroupFamilyDetailModal.vue";
import SendMessageGroupOtherUserGroupMemberDetailModal from "@/pc/components/modals/SendMessageGroupOtherUserGroupMemberDetailModal.vue";
import SendMessageGroupTable, { SendMessageGroupTableField, SendMessageGroupTableItem } from "@/pc/containers/sendMessageGroups/SendMessageGroupTable.vue";

/**
 * 宛先グループ一覧画面コンポーネント
 */
@Component({
  components: {
    HelpIcon,
    PageLoading,
    OkCancelModal,
    OkModal,
    SendMessageGroupFamilyDetailModal,
    SendMessageGroupOtherUserGroupMemberDetailModal,
    SendMessageGroupTable
  },
  name: "SendMessageGroupList"
})
export default class SendMessageGroupList extends Vue {
  /** 宛先グループサービス */
  @Inject("SendMessageGroupsService") sendMessageGroupsService!: SendMessageGroupsService;
  /** 宛先グループメンバーサービス */
  @Inject("SendMessageGroupMembersService") sendMessageGroupMembersService!: SendMessageGroupMembersService;
  /** 施設関係者グループ種類サービス */
  @Inject("OtherUserGroupTypesService") otherUserGroupTypesService!: OtherUserGroupTypesService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 削除確認モーダル表示可否 */
  showDeleteModal: boolean = false;
  /** 削除完了モーダル表示可否 */
  showDeleteSuccessModal: boolean = false;

  /** 宛先グループ列名 */
  fields: Array<SendMessageGroupTableField> = [];
  /** 宛先グループ各行の情報 */
  items: Array<SendMessageGroupTableItem> = [];
  /** 読込み中か否か */
  isLoading: boolean = true;
  /** 削除対象の宛先グループID */
  deleteTargetId: number = 0;
  /** 削除モーダルに表示するメッセージ */
  deleteMessage: string = "";
  /** 施設関係者グループ種類MAP */
  otherUserGroupTypeNameMap: Map<number, string> = new Map<number, string>();
  /** モーダル表示対象の宛先グループ */
  modalGroupMembers: Array<SendMessageGroupMembersFamilyGetModel> | Array<SendMessageGroupMembersOtherUserGroupMemberGetModel> = [];
  /** モーダル表示対象の宛先グループ名 */
  modalGroupName: string = "";

  /** ページングの現在ページ番号 */
  currentPage: number = 1;
  /** ページングの全体行数 */
  rows: number = 0;
  /** 1ページに表示する行数 */
  perPage: number = Number(Setting.sendMessageGroup.perPage);
  /** 1ページに表示する行数の選択項目 */
  pageOptions: number[] = Setting.sendMessageGroup.pageOptions;
  /** 先行して取得するページ範囲 */
  pageRange: number = Setting.sendMessageGroup.pageRange;

  /** 権限ロールを返却する */
  get getRole(): Role {
    return userStore.loginUser.role;
  }

  get _showControlButton(): boolean {
    return userStore.user.permissions.functions.sendMessageGroupsFunction;
  }

  /**
   * ### 1ページあたりの表示行数の変更イベント
   *
   * - 変更をクッキーに持たせる
   */
  @Watch("perPage")
  public changePerPage(perPage: number): void {
    this.setCookie(perPage);
    return;
  }

  /**
   * ### 1ページあたりの表示行数をCookieに設定する
   */
  public setCookie(perPage: number): void {
    // Cookie有効秒数。有効期限は必ず設定しないといけないので、10年分にしておく
    const saveSecond = 10 * 365 * 24 * 60 * 60;
    document.cookie = `send-message-group-list-per-page=${perPage}; max-age=${saveSecond};`;
    return;
  }

  /**
   * ### vue lifecycle mounted
   *
   * - 画面表示情報の作成を行う
   */
  public async mounted(): Promise<void> {
    // 画面初期表示時に作成して変更しないものを初期化する
    const fields: Array<SendMessageGroupTableField> = this.createFields();
    this.fields = fields;
    // 1ページあたりの表示行数はCookieから取得する。取得できない場合はデフォルト値を使用する
    const perPage: number | null = this.loadCookie();
    this.perPage = perPage ? perPage : Number(Setting.sendMessageGroup.perPage);

    await this.initialize();

    return;
  }

  /**
   * ### 宛先グループの列名を作成する
   */
  public createFields(): Array<SendMessageGroupTableField> {
    let fields: Array<SendMessageGroupTableField> = [];

    fields.push({ key: "type", label: "種別", sortable: false, sortDirection: "desc", class: "type text-center" });
    fields.push({ key: "name", label: "グループ名", sortable: false, sortDirection: "desc", class: "name" });
    if (this._showControlButton) {
      fields.push({ key: "operation", label: "登録情報", sortable: false, sortDirection: "desc", class: "operation text-center" });
    }
    fields.push({ key: "member", label: "所属メンバー", sortable: false, sortDirection: "desc", class: "member text-center" });

    return fields;
  }

  /**
   * ### Cookieを見て、値が保持されている場合はCookieの値を返す
   */
  public loadCookie(): number | null {
    if (document.cookie) {
      let cookieMap: any = {};
      let cookies: Array<string> = document.cookie.split("; ");
      cookies.forEach(function (value) {
        //cookie名と値に分ける
        let content = value.split("=");
        cookieMap[content[0]] = content[1];
      });

      // cookieに値が格納されていないとundefinedが格納されているが、実際にはゴミデータが格納されているのでisNaN()でチェックが必要
      const perPage: number = cookieMap["send-message-group-list-per-page"];

      return isNaN(perPage) ? null : perPage;
    }

    return null;
  }

  /**
   * ### グループIDとグループ名のMAPを作成する
   */
  public async createOtherUserGroupTypeNameMap(): Promise<Map<number, string>> {
    const otherUserGroupTypes: Array<OtherUserGroupTypeModel> = await this.otherUserGroupTypesService.get(userStore);

    let typeMap: Map<number, string> = new Map<number, string>();
    for (const type of otherUserGroupTypes) {
      typeMap.set(type.id, type.name);
    }

    return typeMap;
  }

  /**
   * ### 画面初期化
   */
  public async initialize(): Promise<void> {
    this.isLoading = true;

    this.items = [];
    this.currentPage = 1;

    await this.createView();

    this.isLoading = false;

    return;
  }

  /**
   * ### 画面情報作成
   */
  public async createView(): Promise<void> {
    // 施設関係者のグループIDとグループ名の変換用にグループ情報のMAPを作る
    const otherUserGroupTypeNameMap: Map<number, string> = await this.createOtherUserGroupTypeNameMap();
    this.otherUserGroupTypeNameMap = otherUserGroupTypeNameMap;

    // 全体の行数を非同期で取得する
    this.setRows();

    // 宛先グループテーブルに渡す各プロパティを作成する
    const sendMessageGroups: Array<SendMessageGroupsGetModel> = await this.getSendMessageGroups();
    const items: Array<SendMessageGroupTableItem> = this.createItems(sendMessageGroups);
    this.items = items;

    return;
  }

  /**
   * ### ページング用の全件数を設定する
   */
  public async setRows() {
    try {
      const count: number = await this.sendMessageGroupsService.getCount(userStore);
      this.rows = count;
    } catch (error) {
      // 本処理はawaitされない非同期処理なので、共通エラーハンドラーが例外をキャッチしない
      // 例外発生時は自分でエラーハンドラーを呼ぶ
      errorHandle(error, this);
    }

    return;
  }

  /**
   * ### 宛先グループを取得する
   */
  public async getSendMessageGroups(): Promise<Array<SendMessageGroupsGetModel>> {
    let response: Array<SendMessageGroupsGetModel> = [];

    try {
      const params: any = {
        order: "asc"
      };
      response = await this.sendMessageGroupsService.gets(userStore, params);
    } catch (error) {
      const systemerror: SystemError = error as SystemError;
      // 413エラーの場合、全件取得した結果がペイロード制限に引っかかったことになるので、index指定で段階的に取得する
      if (systemerror.statusCode == 413) {
        const count: number = await this.sendMessageGroupsService.getCount(userStore);
        const loopEnd: number = Math.ceil(count / 300);

        for (let i = 0; i < loopEnd; i++) {
          const params: any = {
            startIndex: i * 300,
            endIndex: i * 300 + 300,
            order: "asc"
          };
          response = response.concat(await this.sendMessageGroupsService.gets(userStore, params));
        }
      }
    }
    return response;
  }

  /**
   * ### 宛先グループの行を作成する
   */
  public createItems(sendMessageGroups: Array<SendMessageGroupsGetModel>): Array<SendMessageGroupTableItem> {
    let items: Array<SendMessageGroupTableItem> = [];

    for (const group of sendMessageGroups) {
      items.push({
        id: group.id,
        type: group.type,
        name: group.name,
        members: group.members,
        updated: group.updated
      });
    }

    return items;
  }

  /**
   * ### vue lifecycle activated
   */
  public async activated(): Promise<void> {
    if (needRefreshesStore.sendMessageGroupList.sendMessageGroupList == true) {
      await this.createView();
      needRefreshesStore.sendMessageGroupList.sendMessageGroupList = false;
    }
    return;
  }

  /**
   * ### 1ページあたりの表示行数変更イベント
   *
   * - 1ページあたりの表示行数を更新
   * - ページあたりの表示行数が変わるので表の情報を作り直す
   */
  public async selectPerPage(perPage: number): Promise<void> {
    this.perPage = perPage;
    this.currentPage = 1;
    return;
  }

  /**
   * ### ページネーションのページ番号押下
   */
  public async selectPage(page: number): Promise<void> {
    this.currentPage = page;
    return;
  }

  /**
   * ### 宛先グループ登録画面へ遷移
   */
  public gotoSendMessageGroupRegister(): void {
    this.$router.push(PcUrl.SendMessageGroupRegister);
    return;
  }

  /**
   * ### 宛先グループ編集画面へ遷移
   */
  public gotoSendMessageGroupEdit(group: SendMessageGroupTableItem): void {
    this.$router.push({ name: PcUrl.SendMessageGroupEdit, params: { id: `${group.id}` } });
    return;
  }

  /**
   * ### 宛先グループメンバー編集画面へ遷移
   */
  public gotoSendMessageGroupMemberEdit(group: SendMessageGroupTableItem): void {
    this.$router.push({ name: PcUrl.SendMessageGroupMemberEdit, params: { id: `${group.id}` } });
    return;
  }

  /**
   * ### 削除確認モーダルを表示する
   *
   * - 削除メッセージは動的に作成する
   */
  public showConfirmDeleteModal(group: SendMessageGroupTableItem): void {
    const groupName: string = group.name ? group.name : "";
    this.deleteMessage = `${groupName}を削除しますか？`;
    this.showDeleteModal = true;

    this.deleteTargetId = group.id;

    return;
  }

  /**
   * ### 宛先グループを削除する
   */
  public async deleteSendMessageGroup(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "削除中...";

    await this.sendMessageGroupsService.delete(userStore, this.deleteTargetId);

    // itemsから削除するだけで再取得リクエストはしない
    this.items = this.items.concat().filter((item: SendMessageGroupTableItem) => {
      if (item.id == this.deleteTargetId) {
        return false;
      } else {
        return true;
      }
    });
    this.rows--;
    // メッセージ送信の宛先が変更になるので再読み込みさせるようstoreを更新
    let sendMessageState: SendMessagesState = needRefreshesStore.sendMessage;
    sendMessageState.sendMessages = true;
    needRefreshesStore.setSendMessage(sendMessageState);

    // 削除成功ダイアログを表示する
    this.showDeleteSuccessModal = true;
    this.isBusy = false;

    return;
  }

  /**
   * ### 宛先グループに所属する宛先グループをモーダルに表示する
   */
  public async showSendMessageGroupMemberModal(group: SendMessageGroupTableItem): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    const members: Array<SendMessageGroupMembersFamilyGetModel> = (await this.sendMessageGroupMembersService.get(
      userStore,
      group.id
    )) as Array<SendMessageGroupMembersFamilyGetModel>;
    this.modalGroupMembers = members;
    this.modalGroupName = group.name;

    if (group.type == GroupType.FAMILY) {
      const sendMessageGroupFamilyDetailModal: SendMessageGroupFamilyDetailModal | Vue | Element | Vue[] | Element[] = this.$refs.sendMessageGroupFamilyDetailModal;
      if (sendMessageGroupFamilyDetailModal instanceof SendMessageGroupFamilyDetailModal) {
        sendMessageGroupFamilyDetailModal.show();
      }
    } else if (group.type == GroupType.OTHER_USER) {
      const sendMessageGroupOtherUserGroupMemberDetailModal: SendMessageGroupOtherUserGroupMemberDetailModal | Vue | Element | Vue[] | Element[] =
        this.$refs.sendMessageGroupOtherUserGroupMemberDetailModal;
      if (sendMessageGroupOtherUserGroupMemberDetailModal instanceof SendMessageGroupOtherUserGroupMemberDetailModal) {
        sendMessageGroupOtherUserGroupMemberDetailModal.show();
      }
    }

    this.isBusy = false;

    return;
  }

  public async onCsvDownload(item: SendMessageGroupTableItem): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    console.log("id:", item);

    let csv: string = "";
    if (item.type == GroupType.FAMILY) {
      const members: Array<SendMessageGroupMembersFamilyGetModel> = (await this.sendMessageGroupMembersService.get(
        userStore,
        item.id
      )) as Array<SendMessageGroupMembersFamilyGetModel>;
      csv = this.createFamilyGroupCsv(members);
    } else {
      const members: Array<SendMessageGroupMembersOtherUserGroupMemberGetModel> = (await this.sendMessageGroupMembersService.get(
        userStore,
        item.id
      )) as Array<SendMessageGroupMembersOtherUserGroupMemberGetModel>;
      csv = this.createOtherUserGroupCsv(members);
    }

    const fileName: string = this.createCsvFileName(item);
    this.downloadCsv(csv, fileName);

    this.isBusy = false;

    return;
  }

  /**
   * ### 宛先グループに所属する家族一覧CSVを作成する
   *
   * - 1行目の先頭はexcel等での文字化け対策でBOMを付与する
   * - 1行目は項目名を作る
   * - 2行目以降は行情報にあたる情報を作成して出力する
   */
  public createFamilyGroupCsv(members: Array<SendMessageGroupMembersFamilyGetModel>): string {
    const csv: string[] = [];
    const rowNames: string[] = ["送信手段", "利用者グループ", "部屋名", "利用者名", "家族ユーザー名", "続柄", "役割"];
    csv.push(`\ufeff` + `${rowNames.join(",")}`);

    for (const member of members) {
      const line: string[] = [];

      line.push(this.getSenderType(member.senderType));
      line.push(member.groupName);
      line.push(member.room);
      line.push(member.name);
      line.push(member.familyName);
      line.push(member.familyRelation);
      line.push(member.familyPosition ? member.familyPosition : "");

      csv.push(`${line.join(",")}`);
    }
    return csv.join("\n");
  }

  /**
   * ### 宛先グループに所属する施設関係者一覧CSVを作成する
   *
   * - 1行目の先頭はexcel等での文字化け対策でBOMを付与する
   * - 1行目は項目名を作る
   * - 2行目以降は行情報にあたる情報を作成して出力する
   */
  public createOtherUserGroupCsv(members: Array<SendMessageGroupMembersOtherUserGroupMemberGetModel>): string {
    const csv: string[] = [];
    const rowNames: string[] = ["送信手段", "種類", "グループ名", "ユーザー名", "所属"];
    csv.push(`\ufeff` + `${rowNames.join(",")}`);

    for (const member of members) {
      const line: string[] = [];

      line.push(this.getSenderType(member.senderType));
      const typeName: string | undefined = this.otherUserGroupTypeNameMap.get(member.groupTypeId);
      line.push(typeName ? typeName : "");
      line.push(member.name);
      line.push(member.memberName);
      line.push(member.memberOrganization);

      csv.push(`${line.join(",")}`);
    }
    return csv.join("\n");
  }

  /**
   * ### 送信種別を返す
   */
  public getSenderType(senderType: SenderType): string {
    if (senderType == SenderType.LINE) {
      return "LINE";
    } else if (senderType == SenderType.MAIL) {
      return "メール";
    } else {
      return "";
    }
  }

  /**
   * ### CSVファイル名を作成する
   */
  public createCsvFileName(item: SendMessageGroupTableItem): string {
    const date: string = this.$dateUtil.convertDateToString(new Date(), "yyyymmdd");
    const fileName: string = `HitomeQコネクト-宛先グループ-${item.name}-${date}.csv`;
    return fileName;
  }

  /**
   * ### CSVファイルをダウンロードさせる
   *
   * - CSVの内容からblobファイルを作成する
   * - blobファイルの種別はtext/csv
   * - a要素を作ってそれを疑似的にクリックさせることでダウンロードさせる
   */
  public downloadCsv(csv: string, fileName: string): void {
    const blob: Blob = new Blob([csv], { type: "text/csv" });
    let link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
    return;
  }

  /**
   * ### 画面情報を再取得する
   */
  public async refresh(): Promise<void> {
    await this.initialize();
    return;
  }
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";
@import "../../../styles/pc/_mixin.scss";

#send-message-group-list {
  max-width: 980px;
  margin-left: auto;
  margin-right: auto;

  .box-header {
    .title {
      font-size: 22px;
      font-weight: bold;
    }

    .add-btn {
      width: 80px;
    }
  }
  .box-body {
    .refresh-icon {
      @include svg-icon("../../../assets/refresh.svg", $font-base-color, 20px, 20px);
      opacity: 0.5;
      text-shadow: 0 1px 0 #fff;
      cursor: pointer;
    }
    .refresh-icon:hover {
      opacity: 0.75;
    }
  }
}
</style>
